import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import GwSelect from './GwSelect';
import { COORDINATE_CHOICE } from '../../../app/enums';

/**
 * @typedef {object} CoordinateChoiceProps
 * @property {string} name - Nom du champ envoyé pour l'identifiant de la coordonnée (ex : user[coordinateId] ou coordinateId)
 * @property {string} value - Identifiant de la coordonnée actuelle de l'utilisateur (vide si aucune)
 * @property {boolean} edition - Formulaire de modification du profil : sans coordonnée enregistrée, le choix « sans coordonnée » est sélectionné au chargement
 * @property {boolean} required - Si le choix d'une coordonnée est obligatoire
 * @property {string} label - Libellé du champ
 * @property {string} help - Aide de saisie (HTML) affichée sous la liste des coordonnées
 * @property {string} hasLabel - Libellé du choix « avec coordonnée »
 * @property {string} noneLabel - Libellé du choix « sans coordonnée » (vide = choix non proposé)
 * @property {boolean} suggestion - Si l'utilisateur peut proposer une coordonnée absente de la liste
 * @property {string} suggestionLabel - Libellé du choix de proposition d'une coordonnée
 * @property {string} suggestionHelp - Aide de saisie (HTML) de la proposition d'une coordonnée
 */

const API_URL = '/api/coordinate/gwselect/search';

/**
 * Choix de la coordonnée de l'utilisateur dans les formulaires d'inscription et de modification du profil :
 * une coordonnée de la liste, une coordonnée proposée par l'utilisateur ou aucune coordonnée
 * @param {CoordinateChoiceProps} props - Propriétés du composant
 * @returns {any} - Composant React
 */
function CoordinateChoice({ name, value, edition, required, label, help, hasLabel, noneLabel, suggestion, suggestionLabel, suggestionHelp }) {
  const choices = [
    { value: COORDINATE_CHOICE.HAS.value, label: hasLabel },
    ...(suggestion ? [{ value: COORDINATE_CHOICE.SUGGEST.value, label: suggestionLabel }] : []),
    ...(noneLabel ? [{ value: COORDINATE_CHOICE.NONE.value, label: noneLabel }] : [])
  ];
  const [ currentChoice, setCurrentChoice ] = useState(edition && !value && noneLabel ? COORDINATE_CHOICE.NONE.value : COORDINATE_CHOICE.HAS.value);
  const phoneInputRef = useRef(/** @type {any} */(null));

  // NOTE le champ téléphone n'existe pas au chargement de la page (affiché avec la proposition) : il ne reçoit pas le sélecteur de pays de PhoneInput.js, on l'initialise ici avec les mêmes options
  useEffect(() => {
    const phoneInput = phoneInputRef.current;
    const intlTelInput = /** @type {any} */(window).intlTelInput;

    if (phoneInput && intlTelInput && !phoneInput.iti) {
      phoneInput.iti = intlTelInput(phoneInput, {
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js',
        initialCountry: 'fr',
        separateDialCode: true
      });
      phoneInput.parentNode.style.width = '100%';
    }
  }, [currentChoice]);

  return (
    <div className="input-group input-group--no-margin">
      <p className="input-group__label">{required ? '* ' : ''}{label}</p>

      {choices.length > 1 && (
        <div className="input-group__choices" role="radiogroup" aria-label={label}>
          {choices.map(item => (
            <label key={item.value} className={`input-group__choice${ currentChoice === item.value ? ' input-group__choice--checked' : '' }`}>
              <input type="radio" name="coordinatechoice" value={item.value} checked={currentChoice === item.value} onChange={() => setCurrentChoice(item.value)} />
              {item.label}
            </label>
          ))}
        </div>
      )}

      {currentChoice === COORDINATE_CHOICE.HAS.value && (
        <div className="input-group input-group--no-margin">
          {choices.length > 1 && <div className="space--8"></div>}
          <GwSelect apiUrl={API_URL} apiParams={value ? `includeId=${ value }` : ''} inputNameToSend={name} inputOutputValue={value} inputPlaceholder={required ? label : 'Aucune valeur'} required={required ? 'true' : 'false'} selectFirstOption={required} />
          {help && <p className="input-group__help" dangerouslySetInnerHTML={{ __html: help }}></p>}
        </div>
      )}

      {currentChoice === COORDINATE_CHOICE.SUGGEST.value && (
        <div>
          <div className="space--8"></div>
          <div className="padding--16 background--grey-100 border border--grey-300">
            <p className="paragraph paragraph--no-margin paragraph--bold-600">{label}</p>
            <div className="space--8"></div>
            <div className="grid grid--16-gap grid--has-2-child grid--has-1-child-mobile">
              <div className="input-group input-group--no-margin">
                <label htmlFor="suggestedcoordinatename" className="input-group__label">* Nom</label>
                <input type="text" id="suggestedcoordinatename" name="suggestedcoordinatename" className="input-group__input" placeholder="Nom" data-type="text" data-required="true" />
              </div>
              <div className="input-group input-group--no-margin">
                <label htmlFor="suggestedcoordinateaddress" className="input-group__label">* Adresse</label>
                <input type="text" id="suggestedcoordinateaddress" name="suggestedcoordinateaddress" className="input-group__input" placeholder="Adresse" data-type="text" data-required="true" />
              </div>
              <div className="input-group input-group--no-margin">
                <label htmlFor="suggestedcoordinatepostcode" className="input-group__label">* Code postal</label>
                <input type="text" id="suggestedcoordinatepostcode" name="suggestedcoordinatepostcode" className="input-group__input" placeholder="Code postal" data-type="text" data-required="true" />
              </div>
              <div className="input-group input-group--no-margin">
                <label htmlFor="suggestedcoordinatecity" className="input-group__label">* Ville</label>
                <input type="text" id="suggestedcoordinatecity" name="suggestedcoordinatecity" className="input-group__input" placeholder="Ville" data-type="text" data-required="true" />
              </div>
              <div className="input-group input-group--no-margin">
                <label htmlFor="suggestedcoordinateemail" className="input-group__label">E-mail</label>
                <input type="text" id="suggestedcoordinateemail" name="suggestedcoordinateemail" className="input-group__input" placeholder="E-mail" data-type="email" data-required="false" />
              </div>
              <div className="input-group input-group--no-margin">
                <label htmlFor="suggestedcoordinatephone" className="input-group__label">Téléphone</label>
                <input ref={phoneInputRef} type="text" id="suggestedcoordinatephone" name="suggestedcoordinatephone" className="input-group__input" data-type="mobile" data-required="false" />
              </div>
            </div>
            {suggestionHelp && (
              <>
                <div className="space--8"></div>
                <p className="paragraph paragraph--no-margin paragraph--italic paragraph--grey-700" dangerouslySetInnerHTML={{ __html: suggestionHelp }}></p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CoordinateChoice;

var coordinateChoiceNode = document.getElementById('coordinate-choice');
if (coordinateChoiceNode) {
  var root = ReactDOM.createRoot(coordinateChoiceNode);
  var dataset = coordinateChoiceNode.dataset;

  root.render(
    <CoordinateChoice
      name={dataset.name}
      value={dataset.value}
      edition={dataset.edition === 'true'}
      required={dataset.required === 'true'}
      label={dataset.label}
      help={dataset.help}
      hasLabel={dataset.hasLabel}
      noneLabel={dataset.noneLabel}
      suggestion={dataset.suggestion === 'true'}
      suggestionLabel={dataset.suggestionLabel}
      suggestionHelp={dataset.suggestionHelp}
    />
  );
}
